import { firstValueFrom, forkJoin, Observable, from as observableFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PlatformIdmService } from '@platform-idm/sdk-angular';
import { GetSettingsResponse } from '@platform-idm/sdk-javascript';
import { environment } from 'environments/environment';
import { of } from 'rxjs';
import { catchError, map, mergeMap, retryWhen, switchMap } from 'rxjs/operators';
import { UserCustomerService } from '../users/services/user-customer.service';
import { api, retryStrategy } from './api-loaders';
import { ClaimsService } from './claims.service';
import { MessageService } from './message.service';
import { MessageType } from './messageType';
import { ToggleService } from './toggle/toggle.service';

// Extend the federation option interface to include the new DUO properties
interface ExtendedFederationOption {
  type: string;
  logoutUrl?: string;
  sloEnabled?: boolean;
  [key: string]: any; // Allow other existing properties
}

@Injectable()
export class SecurityService {

  constructor(
    private router: Router,
    private messages: MessageService,
    private claimsService: ClaimsService,
    private userCustomerService: UserCustomerService,
    private toggleService: ToggleService,
    private authService: PlatformIdmService,
    private http: HttpClient
  ) {

  }

  async navigateToHome() {

    const homePages = [
      { check: () => this.isCorporateLimitedAdmin(), url: '/users' },
      { check: () => this.isUserManagementAdmin(), url: '/users' },
      { check: () => this.isUserLimitedLocationAdmin(), url: '/users' },
      { check: () => this.isEctUser(), url: '/ect/customers' },
      { check: () => this.isEctReadOnlyUser(), url: '/ect/customers' },
      { check: () => this.isEcdAdmin(), url: '/ect/administration' },
      { check: () => this.isMapping(), url: '/configuration/mapping/locations/' },
      { check: () => this.showMyProfile(), url: '/my-profile' },
      { check: () => this.isServiceLogAdmin(), url: '/service-log' },
      { check: () => this.isResDocMigrateUser(), url: '/resident-doc-move' },
      { check: () => this.isTokenManagerUser(), url: '/tokenManagement' },

    ];

    // User is authenticated and session is active but logging out as the user is not having rights to access any page.
    if (await this.isAuthenticated()) {


      for (let i = 0; i < homePages.length; i++) {
        const hp = homePages[i];

        if (await hp.check().toPromise()) {
          this.router.navigateByUrl(hp.url);
          return;
        }
      }


      this.messages.get().subscribe((msg) => {
        if (msg === MessageType.AppCInitLoaded) {
          this.messages.send(MessageType.Logout);
        }
      });
    } else {
      this.router.navigate(['/login']);
    }
  };

  async loadUserInfoFromClaims() {
    let userClaims;
    let isReceivedFromTokenMgr: boolean;
    let idToken: any = { claims: await this.authService.authentication.getIdClaims() };

    if (idToken.claims && idToken.claims.first_name && idToken.claims.last_name) {
      userClaims = idToken.claims;
      isReceivedFromTokenMgr = true;
    } else {
      userClaims = Promise.resolve(await this.authService.authentication.getClaims());
      isReceivedFromTokenMgr = false;
    }
    this.claimsService.setEmail(userClaims.email);
    this.claimsService.setName(userClaims.name);
    this.claimsService.setPreferredName(userClaims.preferred_username);
    this.claimsService.setSsoId(userClaims.sub);
    if (isReceivedFromTokenMgr) {
      this.claimsService.setFirstName(userClaims.first_name);
      this.claimsService.setLastName(userClaims.last_name);
    } else {
      this.claimsService.setFirstName(userClaims.given_name);
      this.claimsService.setLastName(userClaims.family_name);
    }

  }

  showMyProfile(): Observable<boolean> {
    let showProfile: boolean;
    try {
      showProfile = JSON.parse(environment.showMyProfile);
    } catch (e) {
    }
    return observableFrom(this.isAuthenticated()).pipe(map(res => {
      if (showProfile) {
        return res;
      } else {
        return false;
      }
    }))
  }


  hasAnyGroup(groups: string[]): Observable<boolean> {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      for (var i = 0; i < groups.length; i++) {
        let group = groups[i];
        if (at_groups.indexOf(group) >= 0)
          return true;
      }
      return false;
    }));
  }

  isEctUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.ect.edit') >= 0;
    }))
  }
  isUserManagementLocalAdmin() {
    return this.hasCatGroup('c\\.(.*)\\.locationadmin');
  }
  isUserManagementCorporateAdmin() {
    return this.hasCatGroup('c\\.(.*)\\.admin');
  }
  isUserLocationLimitedAdmin() {
    return this.hasCatGroup('c\\.(.*)\\.locationlimitedadmin');
  }

  skipServices(): Boolean {
    let snapshot = this.router.routerState.snapshot;
    if (snapshot.root.firstChild?.data?.skipServices)
      return true;

    return false;
  }

  isSupportUser() {

    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.support') >= 0;
    }))
  }

  isCustomerCitusEnabled(): Observable<boolean> {
    return this.getCitusEnabledResponse();
  }

  private getCitusEnabledResponse(): Observable<boolean> {

    return this.http.get<{ productAssigned: boolean }>(api.mfaProductAssignment()).pipe(
      retryWhen(retryStrategy()),
      map(response => {
        return response.productAssigned === true;
      }),
      catchError(error => {
        console.error('Error fetching MFA product assignment:', error);
        return of(false);
      })
    );
  }

  isSupportUserWithCitusReadWriteAccess(): Observable<boolean> {
    return forkJoin({
      isSupportUser: this.isSupportUser(),
      isCustomerCitusEnabled: this.isCustomerCitusEnabled()
    }).pipe(
      map(result => result.isSupportUser && result.isCustomerCitusEnabled)
    );
  }




  hasCrmId() {
    return this.hasCatGroup('^c\\.(.*)$');
  }
  isSuperAdmin() {

    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.superadmin') >= 0;
    }))
  }

  isCanViewAnyCustomer(): Observable<boolean> {
    return forkJoin({
      isSupportUser: this.isSupportUser(),
      isSuperAdmin: this.isSuperAdmin()
    }).pipe(switchMap((data) => of(data.isSupportUser || data.isSuperAdmin)));
  }

  isUserListAdmin() {
    return this.isUserManagementCorporateAdmin().pipe(mergeMap(isAdmin => {
      if (isAdmin)
        return of(isAdmin);

      return this.isUserManagementLocalAdmin().pipe(mergeMap(isLocalAdmin => {
        if (isLocalAdmin)
          return of(isLocalAdmin)

        return this.isUserLocationLimitedAdmin().pipe(mergeMap(isLocalLimitedAdmin => {
          if (isLocalLimitedAdmin)
            return of(isLocalLimitedAdmin)

          return this.isCorporateLimitedAdmin();
        }));
      }));
    }));
  }

  isCorporateLimitedAdmin() {
      return this.hasCatGroup('c\\.(.*)\\.limitedadmin');
  }

  isUserManagementAdmin() {
    return this.isUserManagementCorporateAdmin().pipe(mergeMap(isAdmin => {
      if (isAdmin)
        return of(isAdmin);

      return this.isUserManagementLocalAdmin()
    }));
  }

  isUserLimitedLocationAdmin() {
    return this.isUserLocationLimitedAdmin().pipe(mergeMap(isLocalLimitedAdmin => {
      if (isLocalLimitedAdmin)
        return of(isLocalLimitedAdmin)

      return this.isCorporateLimitedAdmin();
    }));
  }

  isTokenManagerUser() {
    return this.hasCatGroup('c\\.(.*)\\.tokenmgmt');
  }
  isReportsUser() {
    return this.hasCatGroup('c\\.(.*)\\.admin');
  }


  isManage2FactorAuthentication() {
    const $isAdmin = this.isUserManagementCorporateAdmin();
    return $isAdmin.pipe(mergeMap(isAdmin => {
      if (!isAdmin)
        return of(false);

      return this.userCustomerService.getSettings().pipe(map(x => x.mfaEnabled));
    }));
  }
  isManageRulesUser() {
    const $isAdmin = this.isUserManagementCorporateAdmin();
    return $isAdmin.pipe(mergeMap(isAdmin => {
      if (!isAdmin)
        return of(false);

      return this.userCustomerService.getSettings().pipe(map(x => x.mfaEnabled));
    }));
  }

  isManageIPUser(): Observable<boolean> {
    const $isAdmin = this.isUserManagementCorporateAdmin();
    return $isAdmin.pipe(mergeMap(isAdmin => {

      if (!isAdmin)
        return of(false);

      return this.userCustomerService.getSettings().pipe(map(x => x.zonesLimit >= 1 && x.mfaEnabled));
    }));

  }

  // Extracts the CRM Id from the admin cat_group (e.g. "c.D1000.admin" -> "D1000"); mirrors MatrixCareSSOUtils.getCurrentCustomerId().
  getCrmId(): Observable<string> {
    // Case-insensitive match against the original group text so the captured CRM Id keeps its original casing.
    const exp = /^c\.([^.]+)\.(admin|locationadmin|limitedadmin|locationlimitedadmin)$/i;

    return this.getToken().pipe(map(token => {
      if (!token)
        return null;

      var groups: string[] = token['cat_groups'];
      if (!groups)
        return null;
      for (let i = 0; i < groups.length; i++) {
        const match = exp.exec(groups[i]);
        if (match == null) {
          continue;
        }
        return match[1];
      }

      return null;
    }));
  }

  hasCatGroup(catGroupRegEx: string) {
    const exp: any = new RegExp(catGroupRegEx);

    return this.getToken().pipe(map(token => {
      if (!token)
        return false;

      var groups: string[] = token['cat_groups'];
      if (!groups)
        return false;
      for (let i = 0; i < groups.length; i++) {
        const groupName = groups[i].toLowerCase();
        const match = exp.exec(groupName);
        if (match == null) {
          continue;
        }
        return true;
      }

      return false;
    }));
  }
  isServiceLogAdmin() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.log') >= 0;
    }))
  }

  isFederatedUser() {

    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      return token['idmFederation'];
    }))
  }

  mfaEnabled() {
    return this.hasCatGroup('c\\.(.*)\\.mfa');
  }

  //isFederatedUser() {
  //  return this.getToken().pipe(map(token => {
  //    if (!token)
  //      return false;
  //    return token['idmFederation'];
  //  }))
  //}

  isResDocMigrateUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.resdocmigrate') >= 0;
    }))
  }

  isProfileSyncUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.profile.sync') >= 0;
    }))
  }

  isSnfLegacyListUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.migration') >= 0;
    }))
  }

  isSnfLegacyImportUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.migration.import') >= 0;
    }))
  }

  isKeycloakListUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.migration') >= 0;
    }))
  }

  isKeycloakImportUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.migration.import') >= 0;
    }))
  }

  isImportUsers() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;
      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.at.user.migration.import') >= 0;
    }))
  }

  isEcdAdmin() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;

      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.ect.admin') >= 0;
    }))
  }

  isEctReadOnlyUser() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;

      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.ect.read') >= 0;
    }))
  }

  isMapping() {
    return forkJoin({
      toggle: this.toggleService.get(),
      isSCIMMapping: this.hasCatGroup('c\\.(.*)\\.scim.mapping')

    }).pipe(map(x => {
      return x.toggle.azureADEnabled && x.isSCIMMapping;
    }))
  }

  async isThisReadOnlyUser() {
    var isEctReadUser = await this.isEctReadOnlyUser().toPromise();
    var isEctEditUser = await this.isEctUser().toPromise();
    if (isEctEditUser) {
      return false;
    }
    if (isEctReadUser) {
      return true;
    }
    return false;
  }

  isIdpAdmin() {
    return this.getToken().pipe(map(token => {
      if (!token)
        return false;

      var at_groups: string[] = token['at_groups'];
      if (!at_groups)
        return false;

      return at_groups.indexOf('mc.emp.idp.admin') >= 0;
    }))
  }
  canManageSessions() {

    return forkJoin({
      isSupportUser: this.isSupportUser(),
      hasCrmId: this.hasCrmId()
    }).pipe(map(x => {
      return x.isSupportUser && x.hasCrmId;
    }))

  }


  async getAvailableDomains() {
      let domains = (await this.authService.authentication.getClaims()).cat_domain;
      if (!domains)
        return [];

      return domains.split(',').map(x => x.trim());

  }




  private getToken() {
      return observableFrom(this.authService.authentication.getClaims());
  }


  public async getUserId() {
    let token = await this.getToken().toPromise();
    return <String>token.uid;
  }

  public async hasSomeRights() {
    if (await this.isEctUser().toPromise() === true)
      return true;

    if (await this.isUserManagementAdmin().toPromise() === true)
      return true;

    if (await this.isCorporateLimitedAdmin().toPromise() === true)
      return true;

    if (await this.isEcdAdmin().toPromise() === true)
      return true;

    if (await this.isServiceLogAdmin().toPromise() === true)
      return true;

    if (await this.isEctReadOnlyUser().toPromise() === true)
      return true;
    return true;
  }


  // https://stackoverflow.com/questions/2820249/base64-encoding-and-decoding-in-client-side-javascript
  private decodeBase64(text: string) {
    let e = {}, i, b = 0, c, x, l = 0, a, r = '', w = String.fromCharCode, L = text.length;
    let A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    for (i = 0; i < 64; i++) {
      e[A.charAt(i)] = i;
    }
    for (x = 0; x < L; x++) {
      c = e[text.charAt(x)];
      b = (b << 6) + c;
      l += 6;
      while (l >= 8) {
        ((a = (b >>> (l -= 8)) & 0xff) || (x < (L - 2))) && (r += w(a));
      }
    }
    return r;
  }

  public decodeToken(token: string) {

    if (!token) {
      return null;
    }

    const parts = token.split('.');
    const header = parts[0];
    const payload = parts[1];
    const decoded = this.decodeBase64(payload);

    const value = JSON.parse(decoded);
    return value;
  }

  // Checks if access token and id token exists in the token manager. If not, redirect to the login widget.
  async isAuthenticated(): Promise<boolean> {
    return Promise.resolve(this.authService.authentication.isAuthenticated());
  }


  private async getSharedLoginSettings(): Promise<GetSettingsResponse> {
    const authentication = this.authService.authentication as any;
    if (typeof authentication.postSettings === 'function') {
      return authentication.postSettings();
    }

    if (typeof authentication.getClaims !== 'function' || typeof authentication.getOptions !== 'function') {
      if (typeof authentication.getSettings === 'function') {
        return authentication.getSettings();
      }

      throw new Error('Shared login settings methods are unavailable.');
    }

    const claims = await authentication.getClaims();
    const options = authentication.getOptions();

    try {
      return await firstValueFrom(this.http.post<GetSettingsResponse>(
        `${options.sso_serviceUrl}/api/auth/settings`,
        {
          app: options.app,
          username: claims?.sub ?? null,
          configId: options.configId ?? null,
          authPerCustomer: options.authPerCustomer ?? false,
          crmId: options.crmId ?? null
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      ));
    } catch (error) {
      if (typeof authentication.getSettings === 'function') {
        return authentication.getSettings();
      }

      throw error;
    }
  }

	  _isDUOFederated: boolean | null = null;
	  _duoLogoutUrl: string | null = null;
	  _federatedLogoutUrl: string | null = null;

	  async isDUOFederated(): Promise<boolean>{
    if (this._isDUOFederated != null) {
      return this._isDUOFederated;
    }
    if (!this.authService.authentication.isAuthenticated()) {
      return Promise.resolve(false);
     }

	    var claims = await this.authService.authentication.getClaims();
    if (!claims.sub) {
      return Promise.resolve(false);
    }

	    // Only treat the current session as federated if the token indicates it.
	    // This prevents non-federated Okta users from being routed through the
	    // federated SLO flow just because a federation option is configured for
	    // the customer in Okta/IDM settings.
	    const isFederatedSession = !!(claims as any)['idmFederation'];
	    if (!isFederatedSession) {
	      this._isDUOFederated = false;
	      this._duoLogoutUrl = null;
	      this._federatedLogoutUrl = null;
	      return false;
	    }

      let userSettingsResponse: GetSettingsResponse = await this.authService.authentication.getSettings();
	    if (userSettingsResponse) {
	      if (Array.isArray(userSettingsResponse.federationOptions) && userSettingsResponse.federationOptions.length > 0) {
	        const federationOption = userSettingsResponse.federationOptions[0] as ExtendedFederationOption;

	        if (federationOption.type === 'Duo') {
	          // DUO-specific case
	          this._isDUOFederated = true;

	          // Store the DUO logout URL only when SLO is enabled
	          if (federationOption.sloEnabled && federationOption.logoutUrl) {
	            this._duoLogoutUrl = federationOption.logoutUrl;
	          }
	          return true;
	        }

	        // Non-DUO federated provider (e.g., Azure). This user is federated but not DUO.
	        // Capture a generic federated logout URL when SLO is enabled so the app
	        // can route them through the SLO pipeline as well.
	        this._isDUOFederated = false;
	        if (federationOption.sloEnabled && federationOption.logoutUrl) {
	          this._federatedLogoutUrl = federationOption.logoutUrl;
	        }
	        return false;
	      } else {
	        this._isDUOFederated = false;
	        return false;
	      }
	    } else {
	      this._isDUOFederated = false;
	      return false;
	    }
	  }

	  /**
	   * Returns the currently-resolved federated logout URL, if any.
	   *
	   * NOTE: This value is populated as a side effect of calling
	   * isDUOFederated(), which performs the federation settings lookup.
	   * Callers that rely on this value should ensure isDUOFederated()
	   * has been awaited at least once in the current session.
	   */
	  getDuoLogoutUrl(): string | null {
	    return this._duoLogoutUrl;
	  }

	  getFederatedLogoutUrl(): string | null {
	    // Prefer DUO-specific URL if present, otherwise generic federated one.
	    // See note above: callers should invoke isDUOFederated() first to
	    // trigger the federation settings lookup that populates these fields.
	    return this._duoLogoutUrl || this._federatedLogoutUrl;
	  }

}
